import React, { createContext, useContext, useState, useEffect } from "react";
import { useSocket } from "../../context/SocketContext";
import apiClient from "../../config/axios";

const POSContext = createContext();

export const POSProvider = ({ children }) => {
  const { socket, isConnected } = useSocket();
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch data function
  const fetchData = async () => {
    setLoading(true);
    try {
      const ordersRes = await apiClient.get('/pos').catch(() => ({ data: [] }));
      setOrders(ordersRes.data || []);
      
      // Fetch menu items if endpoint exists
      try {
        const menuRes = await apiClient.get('/pos/menu/all');
        setMenuItems(menuRes.data?.items || []);
        setCategories(menuRes.data?.categories || []);
      } catch (menuErr) {
        // Menu endpoint might not be fully implemented yet
        console.warn('Menu endpoint not available:', menuErr);
        setMenuItems([]);
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching POS data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  // Socket.io listeners
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleOrderCreated = (order) => {
      setOrders((prev) => [order, ...prev]);
    };

    const handleOrderUpdated = (order) => {
      setOrders((prev) => prev.map((o) => (o._id === order._id || o.id === order.id ? order : o)));
    };

    const handleMenuItemCreated = async (item) => {
      setMenuItems((prev) => {
        const exists = prev.find(i => i._id === item._id);
        if (exists) return prev;
        return [...prev, item];
      });
      setCategories((prev) => {
        const newCategories = [...prev];
        if (item.category && !newCategories.includes(item.category)) {
          newCategories.push(item.category);
        }
        return newCategories;
      });
      // Refresh menu items to get complete list
      try {
        const menuRes = await apiClient.get('/pos/menu/all');
        setMenuItems(menuRes.data?.items || []);
        setCategories(menuRes.data?.categories || []);
      } catch (err) {
        console.error('Error refreshing menu:', err);
      }
    };

    const handleMenuItemUpdated = async (item) => {
      setMenuItems((prev) => prev.map((i) => (i._id === item._id ? item : i)));
      setCategories((prev) => {
        const newCategories = [...prev];
        if (item.category && !newCategories.includes(item.category)) {
          newCategories.push(item.category);
        }
        return newCategories;
      });
      // Refresh menu items to ensure all data is synced
      try {
        const menuRes = await apiClient.get('/pos/menu/all');
        setMenuItems(menuRes.data?.items || []);
        setCategories(menuRes.data?.categories || []);
      } catch (err) {
        console.error('Error refreshing menu after update:', err);
      }
    };

    const handleMenuItemDeleted = async (data) => {
      setMenuItems((prev) => prev.filter((i) => i._id !== data._id));
      // Refresh menu items to get updated categories
      try {
        const menuRes = await apiClient.get('/pos/menu/all');
        setMenuItems(menuRes.data?.items || []);
        setCategories(menuRes.data?.categories || []);
      } catch (err) {
        console.error('Error refreshing menu:', err);
      }
    };

    socket.on("pos-order-created", handleOrderCreated);
    socket.on("pos-order-updated", handleOrderUpdated);
    socket.on("menu-item-created", handleMenuItemCreated);
    socket.on("menu-item-updated", handleMenuItemUpdated);
    socket.on("menu-item-deleted", handleMenuItemDeleted);

    return () => {
      socket.off("pos-order-created", handleOrderCreated);
      socket.off("pos-order-updated", handleOrderUpdated);
      socket.off("menu-item-created", handleMenuItemCreated);
      socket.off("menu-item-updated", handleMenuItemUpdated);
      socket.off("menu-item-deleted", handleMenuItemDeleted);
    };
  }, [socket, isConnected]);

  return (
    <POSContext.Provider value={{ 
      orders, 
      setOrders,
      menuItems,
      setMenuItems,
      categories,
      setCategories,
      loading,
      refreshData: fetchData
    }}>
      {children}
    </POSContext.Provider>
  );
};

export const usePOS = () => {
  const context = useContext(POSContext);
  if (!context) {
    throw new Error('usePOS must be used within POSProvider');
  }
  return context;
};

